import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';

import AlertsCollection from 'components/AlertsCollection';
import {
  Chart, DisplayMode, Gifs, Image,
} from 'helper/products/synopticCharts';
import {
  selectChart,
  selectDisplayMode,
  selectGifIndex,
  selectGifs,
  selectGifsAreLoading,
  selectImageIndex,
  selectImages,
  selectImagesAreLoading,
  setGifIndex,
  setImageIndex,
} from 'redux/reducers/synopticChartsReducer';
import useGetGifs from 'utils/hooks/synopticCharts/useGetGifs';
import useGetImages from 'utils/hooks/synopticCharts/useGetImages';
import { cssVar } from 'constants/theme';
import DisplayModeToggle from './DisplayModeToggle';
import PentadGifNav from './PentadGifNav';
import ImageNav from './ImageNav';
import FullscreenChart from './FullscreenChart';
import imageDateLabel from './imageDateLabel';

import './index.scss';

export default function ChartViewer() {
  const dispatch = useDispatch();
  const [fullscreen, setFullscreen] = useState(false);
  const chart: Chart | undefined = useSelector(selectChart);
  const displayMode: DisplayMode = useSelector(selectDisplayMode);

  // Disparam o carregamento de dados ao mudar campo/data/runtime (como o Map desktop).
  const { alert: gifAlert } = useGetGifs();
  const { alert: imageAlert } = useGetImages();

  const gifs: Gifs | undefined = useSelector(selectGifs);
  const gifsAreLoading: boolean = useSelector(selectGifsAreLoading);
  const gifIndex: number = useSelector(selectGifIndex);

  const images: Image[] | undefined = useSelector(selectImages);
  const imagesAreLoading: boolean = useSelector(selectImagesAreLoading);
  const imageIndex: number = useSelector(selectImageIndex);

  const isGif = displayMode === DisplayMode.Gif;
  const isLoading = isGif ? gifsAreLoading : imagesAreLoading;
  const dataset = isGif ? gifs : images;
  const index = isGif ? gifIndex : imageIndex;
  const current = dataset && dataset[index];
  const imageUrl = typeof current === 'string' ? current : current?.url;
  const alert = isGif ? gifAlert : imageAlert;

  const forecastLabel = isGif
    ? `GIF · Pêntada ${gifIndex + 1}`
    : imageDateLabel(images?.[imageIndex]?.date);
  const chartAlt = chart?.campo ?? 'Carta sinótica';

  const canNavigate = !!dataset && dataset.length > 1;
  const maxIndex = dataset ? dataset.length - 1 : 0;
  const navigateTo = (target: number) => () => {
    if (isGif) dispatch(setGifIndex(target));
    else dispatch(setImageIndex(target));
  };
  const toPrevious = canNavigate && index > 0 ? navigateTo(index - 1) : undefined;
  const toNext = canNavigate && index < maxIndex ? navigateTo(index + 1) : undefined;

  return (
    <div className="sc-mobile-viewer">
      <div className="sc-mobile-viewer-header">
        <span className="sc-mobile-viewer-title" title={chart?.campo}>
          {chart?.campo || 'Campos Sinóticos'}
        </span>
        {forecastLabel && <span className="sc-mobile-viewer-fct">{forecastLabel}</span>}
      </div>

      <DisplayModeToggle />

      {isGif && <PentadGifNav />}

      <div className="sc-mobile-viewer-body">
        {isLoading && (
          <div className="sc-mobile-viewer-loading">
            <Spinner />
          </div>
        )}

        {!isLoading && alert && !imageUrl && (
          <div className="sc-mobile-viewer-alert">
            <AlertsCollection alerts={[alert]} />
          </div>
        )}

        {!isLoading && imageUrl && (
          <>
            <button
              type="button"
              className="sc-mobile-viewer-image-wrap"
              aria-label="Abrir imagem em tela cheia"
              onClick={() => setFullscreen(true)}
            >
              <span className="sc-mobile-viewer-fullscreen-badge" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={cssVar('text-on-accent')}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 3H4v5M20 8V3h-5M4 16v5h5M15 21h5v-5" />
                </svg>
              </span>
              <img src={imageUrl} alt={chartAlt} className="sc-mobile-viewer-image" />
            </button>

            <FullscreenChart
              open={fullscreen}
              dismiss={() => setFullscreen(false)}
              src={imageUrl}
              alt={chartAlt}
              label={forecastLabel}
              toPrevious={toPrevious}
              toNext={toNext}
            />
          </>
        )}
      </div>

      {!isGif && <ImageNav />}
    </div>
  );
}
